import React from 'react'
import { noop } from 'lodash'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { ApplicationLibraryStatus } from '../../../../graphql/schema/mutations/update-application-library-status'
import { mapReleaseDate } from '../../../../graphql/schema/queries/helpers/library'
import { AgentLanguage } from '../../../../components/agent-language'
import { Application } from '../../../../components/application/api/requests/get-application'
import {
  LibraryAgentLanguage,
  LibraryAgentLanguageValue
} from '../library/library-agent-language'
import { getApplicationLibraryClassUsageCount } from '../../helpers/get-application-library-class-usage-count'
import { LibraryGridPanelID } from '../../helpers/drilldown-ui-interfaces'
import { ApplicationsCell } from '../cell-content/applications-cell'
import { GradeCell } from '../cell-content/grade-cell'
import { LatestVersionCell } from '../cell-content/latest-version-cell'
import { LibraryMergedApplicationsCell } from '../cell-content/library-merged-applications-cell'
import { NameCell } from '../cell-content/name-cell'
import { RowActionsCell } from '../cell-content/row-actions-cell'
import { StatusCell } from '../cell-content/status-cell'
import { UsageCell } from '../cell-content/usage-cell'
import { getUsageCellState } from '../cell-content/usage-cell-state'
import { VulnerabilitiesCell } from '../cell-content/vulnerabilities-cell'
import { CvssScoringType, GridType } from '../../helpers/ui-interfaces'
import {
  LibraryAssessGrid,
  LibraryVulnerability
} from '../../helpers/libraries-interfaces'
import { normalizeBugTrackers } from '../../helpers/object-transforms'
import { Organization, Role } from '@contrast-security-inc/base-types'

interface Props {
  features: any
  appLibraryStatusEnabled: boolean
  organization: Organization
  application: Application | undefined
  applicationID: string | undefined
  setPanelLibrary: ({
    library,
    filterText
  }: {
    library?: LibraryAssessGrid
    filterText?: string
  }) => void
  setSelectedPanelTab: React.Dispatch<React.SetStateAction<LibraryGridPanelID>>
  applicationRole: Role | undefined
  refresh: () => Promise<void>
  gridType: GridType
  serverID?: string
}

const getStatusColumn = ({
  gridType,
  applicationRole,
  organization,
  applicationID,
  refresh,
  appLibraryStatusEnabled
}: {
  gridType: GridType
  applicationRole: Role | undefined
  organization: Organization
  applicationID: string | undefined
  refresh: () => Promise<void>
  appLibraryStatusEnabled: boolean
}) => {
  if (
    gridType !== 'Application' ||
    !applicationID ||
    !appLibraryStatusEnabled
  ) {
    return []
  }

  return [
    {
      accessorKey: 'STATUS',
      id: 'status',
      size: 100,
      enableSorting: false,
      header: () => <FormattedMessage id="title_status" />,
      cell: ({
        row: { original: library }
      }: {
        row: { original: LibraryAssessGrid }
      }) => {
        let status: ApplicationLibraryStatus | undefined

        const application = library.apps
          ? library.apps.find((app) => app.app_id === applicationID)
          : undefined

        if (application) {
          status = application.app_library_status
        }

        if (
          application &&
          library.mergedApps &&
          library.mergedApps.length > 0
        ) {
          status = application.app_library_status
            ? application.app_library_status
            : library.mergedApps[0].app_library_status
        }

        return (
          <div
            className={css`
              width: 294px;
              min-width: 294px;
              max-width: 294px;
            `}
          >
            <StatusCell
              organizationID={organization.organizationUuid}
              applicationRole={applicationRole}
              applicationID={applicationID}
              refresh={refresh}
              rowData={{
                status: status ? status : undefined,
                bugTrackers: normalizeBugTrackers(library.bugtracker_tickets),
                hash: library.hash
              }}
            />
          </div>
        )
      }
    }
  ]
}

const getApplicationColumn = (
  gridType: GridType,
  organization: Organization
) => {
  return gridType !== 'Application'
    ? [
        {
          accessorKey: 'APPLICATIONS',
          id: 'apps',
          size: 50,
          enableSorting: false,
          header: () => <FormattedMessage id="title_applications" />,
          cell: ({
            row: { original: library }
          }: {
            row: { original: LibraryAssessGrid }
          }) => {
            return (
              <ApplicationsCell
                rowData={{
                  organizationID: organization.organizationUuid,
                  applications: library.apps
                    ? library.apps.map(
                        ({
                          app_id: appId,
                          name,
                          app_library_status: appLibraryStatus
                        }) => ({
                          id: `${appId}|${library.hash}`,
                          appId,
                          name,
                          appLibraryStatus
                        })
                      )
                    : [],
                  libraryName: library.file_name
                }}
              />
            )
          }
        }
      ]
    : []
}

const getMergedApplicationColumn = (
  gridType: GridType,
  organization: Organization,
  applicationID: string | undefined,
  application: Application | undefined
) => {
  const showApplicationsColumn = ({ primary }: { primary: boolean }) => {
    if (primary) {
      return true
    }
    return false
  }

  return gridType === 'Application' &&
    applicationID &&
    application &&
    showApplicationsColumn({
      primary: application.primary
    })
    ? [
        {
          accessorKey: 'MERGED_APPLICATIONS',
          enableSorting: false,
          id: 'apps',
          size: 50,
          header: () => <FormattedMessage id="title_applications" />,
          cell: ({
            row: { original: library }
          }: {
            row: { original: LibraryAssessGrid }
          }) => {
            return (
              <LibraryMergedApplicationsCell
                rowData={{
                  organizationID: organization.organizationUuid,
                  libraryName: library.file_name,
                  mergedApplications: library.mergedApps
                    ? library.mergedApps.map(
                        ({
                          app_id: appId,
                          name,
                          app_library_status: appLibraryStatus
                        }) => {
                          return {
                            id: `${appId}|${library.hash}`,
                            appId,
                            name,
                            appLibraryStatus
                          }
                        }
                      )
                    : []
                }}
              />
            )
          }
        }
      ]
    : []
}

export const getColumns = ({
  features,
  organization,
  application,
  applicationID,
  setPanelLibrary,
  setSelectedPanelTab,
  applicationRole,
  organizationRole,
  refresh,
  gridType,
  serverID,
  appLibraryStatusEnabled
}: Props & { organizationRole: Role }) => {
  return [
    {
      accessorKey: 'SCORE',
      id: 'grade',
      enableSorting: true,
      size: 50,
      header: () => <FormattedMessage id="title_score" />,
      cell: ({ row: { original: library } }) => {
        return (
          <div
            className={css`
              width: 24px;
            `}
          >
            <GradeCell
              features={features}
              rowData={{
                language: LibraryAgentLanguage.fromValue(
                  library.app_language as LibraryAgentLanguageValue
                ),
                grade: { score: library.score, value: library.grade }
              }}
            />
          </div>
        )
      }
    },
    {
      accessorKey: 'NAME',
      id: 'file_name',
      enableSorting: true,
      size: 500,
      header: () => <FormattedMessage id="title_library" />,
      cell: ({ row: { original: library } }) => (
        <div>
          {(() => {
            return (
              <>
                {library && (
                  <NameCell
                    organizationID={organization.organizationUuid}
                    appLibraryStatusEnabled={false}
                    rowActiveInPanel={false}
                    applicationId={applicationID}
                    onClick={() => {
                      setPanelLibrary({ library })
                    }}
                    setSelectedPanelTabId={noop}
                    rowData={{
                      name: library.file_name,
                      hash: library.hash,
                      language: library.app_language,
                      ossEnabled: library.ossEnabled,
                      tags: library.tags,
                      bugTrackers: normalizeBugTrackers(
                        library.bugtracker_tickets
                      ),
                      versions: {
                        latest: {
                          version: library.latest_version,
                          releaseDate: mapReleaseDate(
                            library.latest_release_date
                          )
                        },
                        current: {
                          version: library.file_version,
                          releaseDate: mapReleaseDate(library.release_date)
                        }
                      },
                      policies: {
                        invalidVersion: library.invalid_version,
                        restrictedByOrg: library.restricted,
                        restrictedLicense: library.licenseViolation,
                        versionRestriction: library.version_restriction
                      },
                      licenses: library.licenses,
                      usage: { totalUsed: 10 },
                      id: `${library.hash}|${library.app_language}`
                    }}
                  />
                )}
              </>
            )
          })()}
        </div>
      )
    },

    {
      accessorKey: 'LATEST_VERSION',
      id: 'LatestVersion',
      size: 100,
      enableSorting: false,
      cell: ({ row: { original: library } }) => (
        <>
          {library && (
            <LatestVersionCell
              releaseDate={mapReleaseDate(library.latest_release_date)}
              version={library.latest_version}
              compareVersion={library.file_version}
              language={library.app_language}
            />
          )}
        </>
      ),
      header: () => (
        <FormattedMessage id="title_libraryStatistics_latestVersion" />
      )
    },
    {
      accessorKey: 'VULNERABILITIES',
      id: 'Vulnerabilities',
      size: 200,
      enableSorting: false,
      cell: ({ row: { original: library } }) => (
        <div
          className={css`
            min-width: 350px;
          `}
        >
          {(() => {
            return (
              <VulnerabilitiesCell
                rowData={{
                  name: library.file_name,
                  vulnerabilities: library?.vulns
                    ? library.vulns.map((vuln: LibraryVulnerability) => {
                        return {
                          name: vuln.name,
                          description: vuln.description,
                          authentication: vuln.authentication,
                          references: vuln.references,
                          visible: vuln.visible,
                          accessVector: vuln.access_vector,
                          accessComplexity: vuln.access_complexity,
                          availabilityImpact: vuln.availability_impact,
                          confidentialityImpact: vuln.confidentiality_impact,
                          integrityImpact: vuln.integrity_impact,
                          severityValue: parseFloat(
                            vuln.severity_value.toFixed(1)
                          ),
                          severityCode: vuln.severity_code,
                          hasCve: vuln.has_cve,
                          hasCvss3: vuln.has_cvss3_score,
                          cvss3Vector: vuln.cvss_3_vector,
                          cvss3SeverityValue: vuln.cvss_3_severity_value,
                          cvss3SeverityCode: vuln.cvss_3_severity_code
                        }
                      })
                    : []
                }}
                cvssScoringType={
                  organization.cvssScoringType as CvssScoringType
                }
                onClick={() => {
                  setPanelLibrary({ library })
                }}
              />
            )
          })()}
        </div>
      ),
      header: () => <FormattedMessage id="title_vulnerabilitiesCves" />
    },
    ...getApplicationColumn(gridType, organization),
    ...getMergedApplicationColumn(
      gridType,
      organization,
      applicationID,
      application
    ),
    {
      accessorKey: 'USAGE',
      id: 'Usage',
      size: 50,
      enableSorting: true,
      cell: ({ row: { original: library } }) => {
        const isMergedApp = library.mergedApps && library.mergedApps.length > 0
        const classUsageCount = isMergedApp
          ? undefined
          : getApplicationLibraryClassUsageCount({
              applicationID,
              library: {
                libraryClassUsageCounts: library.library_class_usage_counts
              }
            })

        const rowData = {
          usage: {
            total: library.class_count,
            totalUsed: library.classes_used
          },
          applications: library.apps
            ? library.apps.map(
                ({
                  app_id: appId,
                  name,
                  app_library_status: appLibraryStatus
                }) => ({
                  id: `${appId}|${library.hash}`,
                  appId,
                  name,
                  appLibraryStatus
                })
              )
            : []
        }

        const applicationRowData = {
          usage: {
            total: library.class_count,
            totalUsed: library.classes_used
          },
          classUsageCount
        }

        return (
          <UsageCell
            rowData={gridType === 'Application' ? applicationRowData : rowData}
            onClick={
              gridType === 'Application'
                ? () => {
                    setPanelLibrary({ library })
                    setSelectedPanelTab(LibraryGridPanelID.Usage)
                  }
                : undefined
            }
            state={
              /* Note: calculate usage cell state for each render */ getUsageCellState(
                {
                  forApplicationLibrariesGrid: gridType === 'Application',
                  agentFeatures: features,
                  applicationLanguage: AgentLanguage.fromValue(
                    library.app_language
                  )
                }
              )
            }
          />
        )
      },
      header: () => <FormattedMessage id="title_usage" />
    },
    ...getStatusColumn({
      gridType,
      applicationRole,
      organization,
      applicationID,
      refresh,
      appLibraryStatusEnabled
    }),
    {
      accessorKey: 'ACTIONS',
      enableSorting: false,
      id: 'row_actions',
      size: 50,
      header: () => <FormattedMessage id="title_actions_menu" />,
      cell: ({ row: { original: library } }) => (
        <RowActionsCell
          organizationID={organization.organizationUuid}
          library={{
            name: library.file_name,
            hash: library.hash,
            language: LibraryAgentLanguage.fromValue(
              library.app_language as LibraryAgentLanguageValue
            ),
            tags: library.tags,
            applications: library.apps
              ? library.apps.map(
                  ({
                    app_id: appId,
                    name,
                    app_library_status: appLibraryStatus
                  }) => ({
                    id: `${appId}|${library.hash}`,
                    appId,
                    name,
                    appLibraryStatus
                  })
                )
              : []
          }}
          refresh={refresh}
          showDisabled={true}
          organizationRole={organizationRole}
          applicationID={applicationID}
          serverID={serverID}
        />
      )
    }
  ]
}
